import { useState, useCallback } from 'react';
import axios from 'axios';
import constants from '../constants';

interface UseItemDeletionProps {
	companyId: string;
	onSuccess?: () => void;
}

export const useMemberDeletion = <T extends string>({ companyId, onSuccess }: UseItemDeletionProps) => {
	const [selectedItems, setSelectedItems] = useState<T[]>([]);
	const [isDeleting, setIsDeleting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleMemberDelete = useCallback(async () => {
		if (
			!window.confirm(
				'האם אתה בטוח שברצונך למחוק את המשתמשים שנבחרו? \nאזהרה: פעולה זו תמחק את המשתמשים לצמיתות ולא ניתן לשחזר אותם.'
			)
		) {
			return;
		}

		setIsDeleting(true);
		setError(null);

		try {
			const response = await axios.post(`${constants.API_URL}/member/batch-delete`, {
				data: { references: selectedItems },
			});

			const { deletedCount, errors } = response.data;

			if (deletedCount > 0) {
				alert(
					`נמחקו ${deletedCount} משתמשים בהצלחה${errors?.length ? `\n\nשגיאות:\n${errors.join('\n')}` : ''}`
				);
				setSelectedItems([]);
				onSuccess?.();
			} else if (errors?.length) {
				setError(`שגיאה במחיקה: ${errors.join(', ')}`);
			}
		} catch (error: any) {
			console.error('Error during deletion:', error);
			setError('אירעה שגיאה במהלך המחיקה. נסה שוב מאוחר יותר.');
		} finally {
			setIsDeleting(false);
		}
	}, [selectedItems, onSuccess]);

	const handleDelete = useCallback(
		async (endpoint: string) => {
			if (!window.confirm('האם אתה בטוח שברצונך למחוק את הפריטים שנבחרו?')) {
				return;
			}

			setIsDeleting(true);
			setError(null);

			try {
				const response = await axios.delete(`${constants.API_URL}/company/${companyId}/${endpoint}`, {
					data: { values: selectedItems },
				});

				const { deletedCount, errors } = response.data;

				if (deletedCount > 0) {
					alert(
						`נמחקו ${deletedCount} פריטים בהצלחה${
							errors?.length ? `\n\nשגיאות:\n${errors.join('\n')}` : ''
						}`
					);
					setSelectedItems([]);
					onSuccess?.();
				} else if (errors?.length) {
					setError(`שגיאה במחיקה: ${errors.join(', ')}`);
				}

				handleMemberDelete();
			} catch (error: any) {
				console.error('Error during deletion:', error);
				setError('אירעה שגיאה במהלך המחיקה. נסה שוב מאוחר יותר.');
			} finally {
				setIsDeleting(false);
			}
		},
		[companyId, selectedItems, onSuccess]
	);

	const removeItem = useCallback((itemToRemove: T) => {
		setSelectedItems((prev) => prev.filter((item) => item !== itemToRemove));
	}, []);

	const updateItems = useCallback((items: T[]) => {
		setSelectedItems(items);
	}, []);

	return {
		selectedItems,
		isDeleting,
		error,
		setError,
		handleDelete,
		removeItem,
		updateItems,
	};
};
